import Image, { getImageProps, type ImageProps } from "next/image";

const ASSETS_CMS_URLS = [
  "assetscms.foodango.",
  "cms.foodango.",
  "assetscmsqa1.foodango.",
];
const CLOUDFLARE_IMAGE_OPTIONS = "format=auto,quality=auto,onerror=redirect";

const resolveImageSource = (
  src: ImageProps["src"],
): Pick<ImageProps, "src" | "unoptimized"> => {
  if (typeof src !== "string") {
    return { src, unoptimized: false };
  }

  // In the case of images from assetscms.foodango.* (e.g., .pl, .cz) - Skip Next.js optimization
  if (ASSETS_CMS_URLS.some((url) => src.includes(url))) {
    return { src, unoptimized: true };
  }

  // In the case of images from static.caterings.pl - Apply Cloudflare image transformation
  if (src.includes("static.caterings.pl")) {
    const imageUrl = new URL(src);

    return {
      src: `${imageUrl.protocol}//${imageUrl.host}/cdn-cgi/image/${CLOUDFLARE_IMAGE_OPTIONS}${imageUrl.pathname}`,
      unoptimized: true,
    };
  }

  // For all other images, use Next.js optimization
  return { src, unoptimized: false };
};

export const Img = (props: ImageProps) => (
  <Image {...props} {...resolveImageSource(props.src)} />
);

/**
 * `srcSet` for a `<source>` of a `<picture>`, served the same way as `Img` serves
 * that URL. A raw URL in `<source>` would skip both Next.js and Cloudflare
 * image optimization.
 */
export const getImgSrcSet = (src: string, sizes: string): string => {
  const { props } = getImageProps({
    ...resolveImageSource(src),
    alt: "",
    fill: true,
    sizes,
  });

  return props.srcSet ?? props.src;
};
